import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { DriverService } from "../shared/driver.service";
import { LessorService } from "../../lessor/shared/lessor.service";
import { GetPictureService } from "../../shared/services/files/getPictures.service";
import { FileUploadService } from "../../shared/services/files/fileUpload.service";
import { DriverHireDateService } from "../shared/driverHireDate.service";
import { StatesServices } from "../../shared/services/states/states.service";
import { ToastrService } from "ngx-toastr";
import { CanComponentDeactivate } from "../../shared/guards/confirmation/confirmation.guard";
import { DriverValidationService } from "../shared/driverValidation.service";
import { DriverViewModel } from "../../shared/models/drivers/DriverViewModel.model";
import { MaskService } from "../../shared/services/mask/mask.service";
import { authenticationService } from "@app/login/auth.service";
import { Permissions } from "@models/permissions.enum";
import { of as observableOf } from "rxjs";
import { ProductQualificationService } from "@app/product-qualification/shared/product-qualification.service";
import { TractorService } from "@app/tractor/shared/tractor.service";
import { TrailerService } from "@app/trailer/shared/trailer.service";
import { clientService } from "@app/client/shared/client.service";
import { FuelService } from "@app/fuel/fuel.service";
import { DriverTeamsComponent } from "./tabs/driver-teams/driver-teams.component";
import { DriverPeopleNetSummaryModel } from "@app/shared/models/drivers/DriverPeopleNetSummary.model";

@Component({
  selector: "app-driver-detail",
  templateUrl: "./driver-detail.component.html",
  styleUrls: ["./driver-detail.component.css"],
})
export class DriverDetailComponent implements OnInit, CanComponentDeactivate {
  @ViewChild("driverDocuments", { static: true }) driverDocuments;
  @ViewChild("profilePictureInput", { static: false }) profilePictureInput;
  @ViewChild(DriverTeamsComponent, { static: false }) driverTeamsComponent: DriverTeamsComponent;

  driver: DriverViewModel = new DriverViewModel();
  isNewDriver: boolean = false;
  isEditMode: boolean = false;
  isProcessing: boolean = false;
  selectedTab: string = sessionStorage.getItem("driverDetailTab") ?? "profile";
  hasUnsavedChanges: boolean = false;

  // UI state
  profilePicture: any = "assets/img/tractordriver.jpg";
  previewProfilePicture: any;
  newProfileFile: any;
  originalInfo: any;

  // Data
  lessorOwners: any = [];
  productQualifications: any[] = [];
  tractors: any[] = [];
  trailers: any[] = [];
  clients: any[] = [];
  fuelRegions: any[] = [];
  states: { abbreviation: string; fullName: string }[];
  docType: string = "driver";

  // Loading states
  loadingProductQualifications: boolean = true;
  loadingTractors: boolean = true;
  loadingTrailers: boolean = true;
  loadingClients: boolean = true;
  loadingFuelRegions: boolean = true;
  loadingPeopleNetData: boolean = false;
  isSyncingAvatar: boolean = false;

  // PeopleNet data
  peopleNetSummary: DriverPeopleNetSummaryModel | null = null;

  // Masks
  dateInputMask: any;
  phoneMask: any;
  socialSecurityMask: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private driverService: DriverService,
    private lessorService: LessorService,
    private getPictureService: GetPictureService,
    private fileUploadService: FileUploadService,
    private driverHireDateService: DriverHireDateService,
    private statesServices: StatesServices,
    private toastr: ToastrService,
    private driverValidationService: DriverValidationService,
    private maskService: MaskService,
    private authService: authenticationService,
    private productQualificationService: ProductQualificationService,
    private tractorService: TractorService,
    private trailerService: TrailerService,
    private clientService: clientService,
    private fuelService: FuelService
  ) {}

  ngOnInit() {
    this.initializeMasks();
    this.states = this.statesServices.getTwoLettersStates("US");

    // Load dropdown data asynchronously
    this.loadDropdownData();

    this.route.params.subscribe((params) => {
      const driverId = params["driverId"];

      if (driverId === "newdriver") {
        this.initializeNewDriver();
      } else {
        this.loadExistingDriver(driverId);
      }
    });
  }

  initializeMasks() {
    this.dateInputMask = this.maskService.getInputDateOnlyMask();
    this.phoneMask = this.maskService.getUsPhoneMask();
    this.socialSecurityMask = this.maskService.getSocialSecurityMask();
  }

  initializeNewDriver() {
    this.isNewDriver = true;
    this.isEditMode = true;
    this.driver = new DriverViewModel();
    this.driver.driverHireDates = [];
    this.driver.preferredClients = [];
    this.driver.preferredAreas = [];
    this.driver.bannedClients = [];
    this.driver.type = "COMPANY DRIVER";
    this.driver.isActive = true;
    this.driver.isPayTo = true;
  }

  loadExistingDriver(driverId: string) {
    this.isNewDriver = false;
    this.isEditMode = false;

    this.driverService.getDriverInfoById(driverId).subscribe({
      next: (result) => {
        if (!result) {
          this.toastr.error("Driver not found", "Error");
          return;
        }

        this.driver = result;
        this.initDateFields(this.driver);

        if (this.driver.picture) {
          this.profilePicture = this.getFilePath(this.driver.picture);
        }

        this.originalInfo = JSON.stringify(this.driver);

        // Load documents
        if (this.driverDocuments) {
          this.driverDocuments.getDocuments(
            ("web" + this.docType + "_files").toUpperCase(),
            this.driver.id
          );
        }

        // Load PeopleNet data
        this.loadPeopleNetData();
      },
      error: (error) => {
        this.toastr.error("Error loading driver: " + (error?.message || "Unknown error"), "Error");
      },
    });

    // Load lessor list if needed
    this.lessorService.getActiveLessorList(true).subscribe((response) => {
      this.lessorOwners = response || [];
    });
  }

  loadDropdownData() {
    // Load product qualifications
    this.productQualificationService.list(false).subscribe({
      next: (data) => {
        this.productQualifications = data || [];
        this.loadingProductQualifications = false;
      },
      error: () => {
        this.productQualifications = [];
        this.loadingProductQualifications = false;
      },
    });

    // Load tractors
    this.tractorService.listSelectFormat().subscribe({
      next: (data) => {
        this.tractors = data || [];
        this.loadingTractors = false;
      },
      error: () => {
        this.tractors = [];
        this.loadingTractors = false;
      },
    });

    // Load trailers
    this.trailerService.listSelectFormat().subscribe({
      next: (data) => {
        this.trailers = data || [];
        this.loadingTrailers = false;
      },
      error: () => {
        this.trailers = [];
        this.loadingTrailers = false;
      },
    });

    // Load clients
    this.clientService.clientListDropdown().subscribe({
      next: (data) => {
        this.clients = data || [];
        this.loadingClients = false;
      },
      error: () => {
        this.clients = [];
        this.loadingClients = false;
      },
    });

    // Load fuel regions
    this.fuelService.getRegions().subscribe({
      next: (data) => {
        this.fuelRegions = data || [];
        this.loadingFuelRegions = false;
      },
      error: () => {
        this.fuelRegions = [];
        this.loadingFuelRegions = false;
      },
    });
  }

  hasPermission(permissionName: string): boolean {
    const permission = Permissions[permissionName];
    return this.authService.hasPermission(permission);
  }

  canViewProfileTab(): boolean {
    return (
      this.hasPermission("DriversProfileRead") ||
      this.hasPermission("DriversRead") ||
      this.hasPermission("DriversProfileChange")
    );
  }

  canViewComplianceTab(): boolean {
    return (
      this.hasPermission("DriversComplianceRead") || this.hasPermission("DriversComplianceChange")
    );
  }

  canViewNotesTab(): boolean {
    return this.hasPermission("DriversNotesRead") || this.hasPermission("DriversNotesChange");
  }

  canViewScheduleTab(): boolean {
    return this.hasPermission("DriversScheduleRead") || this.hasPermission("DriversScheduleChange");
  }

  canViewAssignmentsTab(): boolean {
    return (
      this.hasPermission("DriversAssignmentsRead") || this.hasPermission("DriversAssignmentsChange")
    );
  }

  canViewTeamsTab(): boolean {
    return this.hasPermission("DriversTeamsRead") || this.hasPermission("DriversTeamsChange");
  }

  canViewSettlementsTab(): boolean {
    return (
      this.hasPermission("DriversSettlementsRead") || this.hasPermission("DriversSettlementsChange")
    );
  }

  canEditProfileTab(): boolean {
    return this.hasPermission("DriversProfileChange");
  }

  canEditComplianceTab(): boolean {
    return this.hasPermission("DriversComplianceChange");
  }

  canEditNotesTab(): boolean {
    return this.hasPermission("DriversNotesChange");
  }

  canEditScheduleTab(): boolean {
    return this.hasPermission("DriversScheduleChange");
  }

  canEditAssignmentsTab(): boolean {
    return this.hasPermission("DriversAssignmentsChange");
  }

  canEditTeamsTab(): boolean {
    return this.hasPermission("DriversTeamsChange");
  }

  canEditSettlementsTab(): boolean {
    return this.hasPermission("DriversSettlementsChange");
  }

  canEditAnyTab(): boolean {
    return (
      this.canEditProfileTab() ||
      this.canEditComplianceTab() ||
      this.canEditNotesTab() ||
      this.canEditScheduleTab() ||
      this.canEditAssignmentsTab() ||
      this.canEditTeamsTab() ||
      this.canEditSettlementsTab() ||
      this.hasPermission("DriversChange")
    );
  }

  onTabClicked(tab: string) {
    this.selectedTab = tab;
    sessionStorage.setItem("driverDetailTab", tab);
  }

  onTabHasChanges(hasChanges: boolean) {
    this.hasUnsavedChanges = hasChanges;
  }

  onTeamSaved() {
    // Reload driver data to get updated earning percentages from the team
    if (this.driver?.id) {
      this.driverService.getDriverInfoById(this.driver.id).subscribe({
        next: (result) => {
          if (result) {
            // Preserve the current edit mode and profile picture
            const wasEditMode = this.isEditMode;
            const currentProfilePic = this.profilePicture;
            const currentPreviewPic = this.previewProfilePicture;
            const currentNewFile = this.newProfileFile;

            this.driver = result;
            this.initDateFields(this.driver);
            this.originalInfo = JSON.stringify(this.driver);

            // Restore UI state
            this.isEditMode = wasEditMode;
            this.profilePicture = currentProfilePic;
            this.previewProfilePicture = currentPreviewPic;
            this.newProfileFile = currentNewFile;
          }
        },
        error: (error) => {
          console.error("Error reloading driver data:", error);
          this.toastr.warning(
            "Team saved but could not refresh driver data. Please refresh the page."
          );
        },
      });
    }
  }

  toggleEditMode() {
    if (!this.isEditMode) {
      this.isEditMode = true;
      this.originalInfo = JSON.stringify(this.driver);
    } else {
      this.saveDriver();
    }
  }

  cancelEdit() {
    if (this.isNewDriver) {
      this.router.navigate(["/drivers-list"]);
    } else {
      this.isEditMode = false;
      this.resetChangesFlag();
      this.driver = JSON.parse(this.originalInfo);
      this.initDateFields(this.driver);

      if (this.driver.picture) {
        this.profilePicture = this.getFilePath(this.driver.picture);
      } else {
        this.profilePicture = "assets/img/tractordriver.jpg";
      }
      this.newProfileFile = null;
      this.previewProfilePicture = null;
    }
  }

  saveDriver() {
    // Check if we're on the teams tab and a team is being edited/created
    if (this.selectedTab === "teams" && this.driverTeamsComponent) {
      if (this.driverTeamsComponent.creating) {
        // Wait for team creation to complete, then reload driver data, then save driver
        this.driverTeamsComponent.createTeam(() => {
          this.reloadDriverDataThenSave();
        });
        return;
      } else if (this.driverTeamsComponent.editing) {
        // Wait for team save to complete, then reload driver data, then save driver
        this.driverTeamsComponent.saveTeam(() => {
          this.reloadDriverDataThenSave();
        });
        return;
      }
    }

    this.proceedWithDriverSave();
  }

  reloadDriverDataThenSave() {
    // Reload driver data to get updated earning percentages from the team
    if (this.driver?.id) {
      this.driverService.getDriverInfoById(this.driver.id).subscribe({
        next: (result) => {
          if (result) {
            // Preserve the current edit mode and profile picture
            const wasEditMode = this.isEditMode;
            const currentProfilePic = this.profilePicture;
            const currentPreviewPic = this.previewProfilePicture;
            const currentNewFile = this.newProfileFile;

            this.driver = result;
            this.initDateFields(this.driver);
            this.originalInfo = JSON.stringify(this.driver);

            // Restore UI state
            this.isEditMode = wasEditMode;
            this.profilePicture = currentProfilePic;
            this.previewProfilePicture = currentPreviewPic;
            this.newProfileFile = currentNewFile;

            // Now proceed with the driver save
            this.proceedWithDriverSave();
          }
        },
        error: (error) => {
          console.error("Error reloading driver data:", error);
          this.toastr.warning(
            "Team saved but could not refresh driver data. Please refresh the page."
          );
        },
      });
    } else {
      // If no driver ID, just proceed
      this.proceedWithDriverSave();
    }
  }

  proceedWithDriverSave() {
    if (!this.validateDriver(this.driver)) {
      return;
    }

    this.isProcessing = true;

    if (this.isNewDriver) {
      this.createNewDriver();
    } else {
      this.updateExistingDriver();
    }
  }

  createNewDriver() {
    let profilePictureObservable;

    if (this.newProfileFile) {
      profilePictureObservable = this.uploadProfilePicture();
    } else {
      profilePictureObservable = observableOf(undefined);
    }

    profilePictureObservable.subscribe((result: any) => {
      if (result) {
        this.driver.picture = result.name;
      }

      // Convert Date objects to DateOnly strings before sending to server
      this.prepareDateOnlyFields();

      this.driverService.createNewDriver(this.driver).subscribe(
        (driverCreated: any) => {
          const documentUpload = this.driverDocuments?.documents?.length
            ? this.uploadDocuments(driverCreated.id)
            : observableOf(null);

          documentUpload.subscribe(() => {
            const hireDateUpload = this.driver.driverHireDates?.length
              ? this.addHireDates(driverCreated.id)
              : observableOf(null);

            hireDateUpload.subscribe(() => {
              this.finishDriverCreation(driverCreated.id);
            });
          });
        },
        (errors) => {
          this.handleErrors(errors);
          this.isProcessing = false;
        }
      );
    });
  }

  updateExistingDriver() {
    if (this.newProfileFile) {
      this.uploadProfilePicture().subscribe((url: any) => {
        this.driver.picture = url.name;
        this.saveDriverChanges();
      });
    } else {
      this.saveDriverChanges();
    }
  }

  async saveDriverChanges() {
    // Check if tractor assignment changed and validate
    if (this.driver.tractorId) {
      const originalDriver = JSON.parse(this.originalInfo);
      if (originalDriver.tractorId !== this.driver.tractorId) {
        const canProceed = await this.checkTractorAssignment();
        if (!canProceed) {
          this.isProcessing = false;
          return;
        }
      }
    }

    // Check if trailer assignment changed and validate
    if (this.driver.trailerId) {
      const originalDriver = JSON.parse(this.originalInfo);
      if (originalDriver.trailerId !== this.driver.trailerId) {
        const canProceed = await this.checkTrailerAssignment();
        if (!canProceed) {
          this.isProcessing = false;
          return;
        }
      }
    }

    const driverId = String(this.driver.id);
    const documentUpload = this.driverDocuments?.documents?.length
      ? this.uploadDocuments(driverId)
      : observableOf(null);

    documentUpload.subscribe({
      next: () => {
        const hireDateUpload = this.driver.driverHireDates?.length
          ? this.addHireDates(driverId)
          : observableOf(null);

        hireDateUpload.subscribe({
          next: () => {
            // Convert Date objects to DateOnly strings before sending to server
            this.prepareDateOnlyFields();

            this.driverService.updateDriver(this.driver).subscribe({
              next: () => {
                this.toastr.success("Driver updated", "Success");
                this.isProcessing = false;
                this.isEditMode = false;
                this.resetChangesFlag();
                this.originalInfo = JSON.stringify(this.driver);
                this.router.navigate(["/drivers-list"]);
              },
              error: (errors) => {
                this.handleErrors(errors);
                this.isProcessing = false;
              },
            });
          },
          error: (error) => {
            this.toastr.warning(error, "Alert!");
            this.isProcessing = false;
          },
        });
      },
      error: () => {
        this.toastr.error("Error uploading files");
        this.isProcessing = false;
      },
    });
  }

  async checkTractorAssignment(): Promise<boolean> {
    try {
      const tractor = await this.tractorService.getTractorAsync(this.driver.tractorId);

      if (tractor.driverId && tractor.driverId !== this.driver.id) {
        // Get the other driver's info
        const otherDriver = await this.driverService.getDriverInfoByIdAsync(tractor.driverId);

        const message = `Tractor ${tractor.unitNumber} is currently assigned to ${otherDriver.name} ${otherDriver.lastName}. Assigning it to this driver will unassign it from ${otherDriver.name} ${otherDriver.lastName}. Do you want to continue?`;

        if (!confirm(message)) {
          return false;
        }

        // Unassign from the other driver
        otherDriver.tractorId = null;
        await this.driverService.updateDriver(otherDriver).toPromise();
      }

      return true;
    } catch (error) {
      this.toastr.error("Error checking tractor assignment");
      return false;
    }
  }

  async checkTrailerAssignment(): Promise<boolean> {
    try {
      const trailer: any = await this.trailerService
        .gettrailerinfo(this.driver.trailerId)
        .toPromise();

      if (trailer.driverId && trailer.driverId !== this.driver.id) {
        // Get the other driver's info
        const otherDriver = await this.driverService.getDriverInfoByIdAsync(trailer.driverId);

        const message = `Trailer ${trailer.trailerNo} is currently assigned to ${otherDriver.name} ${otherDriver.lastName}. Assigning it to this driver will unassign it from ${otherDriver.name} ${otherDriver.lastName}. Do you want to continue?`;

        if (!confirm(message)) {
          return false;
        }

        // Unassign from the other driver
        otherDriver.trailerId = null;
        await this.driverService.updateDriver(otherDriver).toPromise();
      }

      return true;
    } catch (error) {
      this.toastr.error("Error checking trailer assignment");
      return false;
    }
  }

  finishDriverCreation(driverId: string) {
    this.toastr.success("Driver created", "Success");
    this.isProcessing = false;
    this.isNewDriver = false;
    this.isEditMode = false;
    this.resetChangesFlag();
    if (this.driverDocuments) {
      this.driverDocuments.files = [];
    }
    this.router.navigate([`/drivers/${driverId}`]);
  }

  validateDriver(driver: DriverViewModel): boolean {
    let errors = this.driverValidationService.validate(driver);
    if (errors && errors.length > 0) {
      for (const error of errors) {
        this.toastr.warning(error, "Alert!");
      }
      return false;
    }
    return true;
  }

  handleErrors(errors: any) {
    if (Array.isArray(errors)) {
      for (const error of errors) {
        this.toastr.warning(error, "Alert!", {
          closeButton: true,
          enableHtml: true,
        });
      }
    } else {
      this.toastr.error("An error occurred", "Error");
    }
  }

  // Image handling
  onProfilePictureSelected(event: any) {
    if (event.target.files && event.target.files[0]) {
      this.newProfileFile = event.target.files[0];
      this.previewProfilePicture = this.getPictureService.getPreviewImage(event.target.files[0]);
      this.profilePicture = this.previewProfilePicture;
    }
  }

  uploadProfilePicture() {
    const fname: string = this.newProfileFile.name;
    return this.fileUploadService.uploadSingleFileRename(
      [this.newProfileFile],
      this.docType,
      this.driver.driverNo + "_" + fname
    );
  }

  uploadDocuments(driverId: string) {
    return this.driverDocuments.uploadDocuments(
      { DriverId: driverId },
      this.docType + "_files",
      driverId
    );
  }

  addHireDates(driverId: string) {
    return this.driverHireDateService.createHireDates(driverId, this.driver.driverHireDates);
  }

  getFilePath(path: string) {
    return this.getPictureService.getPicturePath(path, this.docType);
  }

  // Driver type change
  driverTypeChanged() {
    if (this.driver.type === "LEASE OPERATOR" && !this.lessorOwners.length) {
      this.lessorService.getActiveLessorList(true).subscribe((response) => {
        this.lessorOwners = response || [];
      });
    }
  }

  // Update driver number based on name
  updatedDriverNo() {
    if (this.driver.name && this.driver.lastName) {
      this.driver.driverNo = (
        this.driver.lastName.slice(0, 4) + this.driver.name.slice(0, 4)
      ).toUpperCase();
    }
  }

  // Date field initialization
  initDateFields(driver: DriverViewModel) {
    // Convert DateOnly strings from YYYY-MM-DD to MM/dd/yyyy for input masks
    driver.birthDate = this.convertDateOnlyToDisplay(driver.birthDate) as any;
    driver.hiredDate = this.convertDateOnlyToDisplay(driver.hiredDate) as any;
    driver.hiredDateTo = this.convertDateOnlyToDisplay(driver.hiredDateTo) as any;
    driver.licenseExpirationDate = this.convertDateOnlyToDisplay(
      driver.licenseExpirationDate
    ) as any;
    driver.physicalExamDate = this.convertDateOnlyToDisplay(driver.physicalExamDate) as any;
    driver.physicalExamExpirationDate = this.convertDateOnlyToDisplay(
      driver.physicalExamExpirationDate
    ) as any;
    driver.annualReviewDate = this.convertDateOnlyToDisplay(driver.annualReviewDate) as any;
    driver.trainingDateCGMP = this.convertDateOnlyToDisplay(driver.trainingDateCGMP) as any;
    driver.expirationDateCGMP = this.convertDateOnlyToDisplay(driver.expirationDateCGMP) as any;
    driver.hazmatTrainingDate = this.convertDateOnlyToDisplay(driver.hazmatTrainingDate) as any;
    driver.hazmatTrainingExpirationDate = this.convertDateOnlyToDisplay(
      driver.hazmatTrainingExpirationDate
    ) as any;
    driver.hmeExpirationDate = this.convertDateOnlyToDisplay(driver.hmeExpirationDate) as any;
    driver.passportExpirationDate = this.convertDateOnlyToDisplay(
      driver.passportExpirationDate
    ) as any;
    driver.inDepthSecurityExpirationDate = this.convertDateOnlyToDisplay(
      driver.inDepthSecurityExpirationDate
    ) as any;
    driver.twicCardExpirationDate = this.convertDateOnlyToDisplay(
      driver.twicCardExpirationDate
    ) as any;
    driver.terminationDate = this.convertDateOnlyToDisplay(driver.terminationDate) as any;

    if (driver.driverHireDates) {
      driver.driverHireDates = driver.driverHireDates.map((hireDate) => {
        return {
          ...hireDate,
          hiredDate: this.convertDateOnlyToDisplay(hireDate.hiredDate) as any,
          hiredDateTo: this.convertDateOnlyToDisplay(hireDate.hiredDateTo) as any,
        };
      });
    }
  }

  convertDateOnlyToDisplay(dateString: any): string | null {
    if (!dateString) return null;

    // If it's in YYYY-MM-DD format, convert to MM/dd/yyyy
    const str = String(dateString);
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [year, month, day] = str.split("-");
      return `${month}/${day}/${year}`;
    }

    // Otherwise return as is
    return dateString;
  }

  convertToDate(date: any): Date | null {
    if (!date) return null;

    // For datetime strings with time component
    return new Date(date);
  }

  // Guard
  confirm(): boolean {
    if (this.hasUnsavedChanges || (this.isEditMode && !this.isNewDriver)) {
      return window.confirm("You have unsaved changes. Are you sure you want to leave this page?");
    } else if (this.isNewDriver) {
      return window.confirm("Leave this page without creating the driver?");
    }
    return true;
  }

  resetChangesFlag() {
    this.hasUnsavedChanges = false;
  }

  convertDateToDateOnlyString(date: any): string | null {
    if (!date) return null;

    // If already in YYYY-MM-DD format, return as is
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }

    // If it's a Date object, convert to YYYY-MM-DD
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    // If it's a string like "MM/dd/yyyy", parse and convert
    if (typeof date === "string") {
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) {
        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, "0");
        const day = String(parsed.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
    }

    return null;
  }

  prepareDateOnlyFields() {
    // Convert all DateOnly fields from Date objects to YYYY-MM-DD strings
    this.driver.birthDate = this.convertDateToDateOnlyString(this.driver.birthDate) as any;
    this.driver.hiredDate = this.convertDateToDateOnlyString(this.driver.hiredDate) as any;
    this.driver.hiredDateTo = this.convertDateToDateOnlyString(this.driver.hiredDateTo) as any;
    this.driver.licenseExpirationDate = this.convertDateToDateOnlyString(
      this.driver.licenseExpirationDate
    ) as any;
    this.driver.physicalExamDate = this.convertDateToDateOnlyString(
      this.driver.physicalExamDate
    ) as any;
    this.driver.physicalExamExpirationDate = this.convertDateToDateOnlyString(
      this.driver.physicalExamExpirationDate
    ) as any;
    this.driver.annualReviewDate = this.convertDateToDateOnlyString(
      this.driver.annualReviewDate
    ) as any;
    this.driver.trainingDateCGMP = this.convertDateToDateOnlyString(
      this.driver.trainingDateCGMP
    ) as any;
    this.driver.expirationDateCGMP = this.convertDateToDateOnlyString(
      this.driver.expirationDateCGMP
    ) as any;
    this.driver.hazmatTrainingDate = this.convertDateToDateOnlyString(
      this.driver.hazmatTrainingDate
    ) as any;
    this.driver.hazmatTrainingExpirationDate = this.convertDateToDateOnlyString(
      this.driver.hazmatTrainingExpirationDate
    ) as any;
    this.driver.hmeExpirationDate = this.convertDateToDateOnlyString(
      this.driver.hmeExpirationDate
    ) as any;
    this.driver.passportExpirationDate = this.convertDateToDateOnlyString(
      this.driver.passportExpirationDate
    ) as any;
    this.driver.inDepthSecurityExpirationDate = this.convertDateToDateOnlyString(
      this.driver.inDepthSecurityExpirationDate
    ) as any;
    this.driver.twicCardExpirationDate = this.convertDateToDateOnlyString(
      this.driver.twicCardExpirationDate
    ) as any;
    this.driver.terminationDate = this.convertDateToDateOnlyString(
      this.driver.terminationDate
    ) as any;

    if (this.driver.driverHireDates) {
      this.driver.driverHireDates = this.driver.driverHireDates.map((hireDate) => {
        return {
          ...hireDate,
          hiredDate: this.convertDateToDateOnlyString(hireDate.hiredDate) as any,
          hiredDateTo: this.convertDateToDateOnlyString(hireDate.hiredDateTo) as any,
        };
      });
    }
  }

  getDisplayType(type: string): string {
    if (type === "LEASE OPERATOR") {
      return "LESSOR";
    }
    return type;
  }

  getAvatarSyncLabel(): string {
    if (!this.driver.lastAvatarSyncDate) return "Avatar sync: never";
    const now = new Date();
    const synced = new Date(this.driver.lastAvatarSyncDate);
    const diffMs = now.getTime() - synced.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Avatar synced: just now";
    if (diffMins < 60) return `Avatar synced: ${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Avatar synced: ${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `Avatar synced: ${diffDays}d ago`;
  }

  triggerAvatarSync(): void {
    this.isSyncingAvatar = true;
    this.driverService.triggerAvatarSync().subscribe({
      next: () => {
        this.driver.lastAvatarSyncDate = new Date() as any;
        this.isSyncingAvatar = false;
        this.toastr.success("Avatar sync complete");
        // Reload driver to pick up updated picture/data
        this.driverService.getDriverInfoById(this.driver.id).subscribe({
          next: (d: any) => { this.driver = d; },
          error: () => {}
        });
      },
      error: () => {
        this.isSyncingAvatar = false;
        this.toastr.error("Avatar sync failed");
      }
    });
  }

  isFirst90Days(): boolean {
    if (!this.driver.hiredDate) return false;
    const hiredDate = new Date(this.driver.hiredDate);
    const today = new Date();
    const daysDiff = Math.floor((today.getTime() - hiredDate.getTime()) / (1000 * 60 * 60 * 24));
    return daysDiff >= 0 && daysDiff <= 90;
  }

  getLessorName(): string {
    if (this.driver.type !== "LEASE OPERATOR" || !this.driver.lessorId) return "";
    const lessor = this.lessorOwners.find((l: any) => l.id === this.driver.lessorId);
    return lessor ? lessor.name : "";
  }

  getDriverStatus(): "on" | "off" {
    return this.driver?.isOffToday ? "off" : "on";
  }

  hasDoNotDispatch(): boolean {
    return this.driver.doNotDispatch === true;
  }

  hasOutOfCompliance(): boolean {
    return this.driver.outOfCompliance === true;
  }

  hasExpiringSoon(): boolean {
    if (!this.driver) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dates = [
      this.driver.licenseExpirationDate,
      this.driver.hazmatTrainingExpirationDate,
      this.driver.passportExpirationDate,
      this.driver.expirationDateCGMP,
      this.driver.hmeExpirationDate,
      this.driver.inDepthSecurityExpirationDate,
      this.driver.twicCardExpirationDate,
    ];

    for (const date of dates) {
      if (!date) continue;
      const expDate = new Date(date);
      expDate.setHours(0, 0, 0, 0);

      if (expDate < today) continue; // Skip expired

      const daysUntilExpiration = Math.floor(
        (expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysUntilExpiration <= 30) {
        return true;
      }
    }

    return false;
  }

  getTractorNumber(): string {
    // Check if there's an assigned tractor in the tractors array
    if (this.driver.tractorId && this.tractors && this.tractors.length > 0) {
      const tractor = this.tractors.find((t) => t.id === this.driver.tractorId);
      return tractor ? tractor.unitNumber : "N/A";
    }
    return "N/A";
  }

  getTrailerNumber(): string {
    // Check if there's an assigned trailer in the trailers array
    if (this.driver.trailerId && this.trailers && this.trailers.length > 0) {
      const trailer = this.trailers.find((t) => t.id === this.driver.trailerId);
      return trailer ? trailer.trailerNo : "N/A";
    }
    return "N/A";
  }

  getActiveProductQualifications(): string[] {
    if (
      !this.driver ||
      !this.driver.productQualifications ||
      this.driver.productQualifications.length === 0
    ) {
      return [];
    }

    return this.driver.productQualifications
      .filter((pq) => pq.qualification.toLowerCase() === "yes")
      .map((pq) => pq.productQualificationName || "")
      .filter((abbrev) => abbrev !== "");
  }

  getPrequalifiedProductQualifications(): string[] {
    if (
      !this.driver ||
      !this.driver.productQualifications ||
      this.driver.productQualifications.length === 0
    ) {
      return [];
    }

    return this.driver.productQualifications
      .filter((pq) => pq.qualification.toLowerCase() === "prequalified")
      .map((pq) => pq.productQualificationName || "")
      .filter((abbrev) => abbrev !== "");
  }

  navigateToList() {
    this.router.navigate(["/drivers-list"]);
  }

  // PeopleNet data loading and formatting methods
  loadPeopleNetData() {
    if (!this.driver?.id || !this.driver?.peopleNetId) {
      this.peopleNetSummary = null;
      return;
    }

    this.loadingPeopleNetData = true;
    this.driverService.getDriverSummary(this.driver.id).subscribe({
      next: (data) => {
        this.peopleNetSummary = data;
        this.loadingPeopleNetData = false;
      },
      error: (error) => {
        console.error("Error loading PeopleNet data:", error);
        this.peopleNetSummary = null;
        this.loadingPeopleNetData = false;
      },
    });
  }

  getCurrentLocation(): string {
    if (this.loadingPeopleNetData) return "";
    if (!this.peopleNetSummary?.mostRecentLocation?.parsedLocation) return "n/a";
    return this.peopleNetSummary.mostRecentLocation.parsedLocation;
  }

  getLast7DaysMiles(): string {
    if (this.loadingPeopleNetData) return "";
    if (this.peopleNetSummary?.totalMiles == null) return "n/a";
    const miles = this.peopleNetSummary.totalMiles;
    return miles.toLocaleString();
  }

  getHoursOfService(): string {
    if (this.loadingPeopleNetData) return "";
    if (this.peopleNetSummary?.rawHoursOfService == null) return "n/a";
    const available = this.peopleNetSummary.rawHoursOfService.dsaUsa708Hours;
    return `${available.toFixed(1)}`;
  }
}
